import React, { useEffect, useMemo, useState, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { settingsActions, useSettingsStore } from '../../data/settingsStore';
type Month = {
  key: string;
  label: string;
};
export function BudgetAllocationsTab() {
  const {
    departments,
    departmentBudgetAllocations
  } = useSettingsStore();
  const [monthOffset, setMonthOffset] = useState(0);
  const activeDepartments = departments.filter(department => department.status === 'active');
  const months = useMemo<Month[]>(() => {
    const current = new Date();
    return Array.from({
      length: 6
    }, (_, index) => {
      const date = new Date(current.getFullYear(), current.getMonth() + monthOffset + index, 1);
      return {
        key: monthKey(date),
        label: date.toLocaleDateString('en-GB', {
          month: 'short',
          year: 'numeric'
        })
      };
    });
  }, [monthOffset]);
  const valueFor = (departmentId: string, key: string) => departmentBudgetAllocations[departmentId]?.[key] || 0;
  const monthlyTotals = months.map(month => activeDepartments.reduce((sum, department) => sum + valueFor(department.id, month.key), 0));
  return <section className="space-y-4" data-id="element-240" data-mp="7wfwb5kn69ea">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-241" data-mp="7vejownz9eug">
        <div data-id="element-242" data-mp="2y4kipasdotf">
          <h2 className="text-base font-semibold text-slate-900" data-id="element-243" data-mp="2yo56drtmrb5">
            Monthly budget allocations
          </h2>
          <p className="mt-0.5 text-sm text-slate-500" data-id="element-244" data-mp="3q9ngejfssf68">
            Set monthly budgets by active department. Totals update as you edit.
          </p>
        </div>
        <MonthNavigation onPrevious={() => setMonthOffset(current => current - 1)} onCurrent={() => setMonthOffset(0)} onNext={() => setMonthOffset(current => current + 1)} data-id="element-245" data-mp="3lc44hq6koehg" />
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-246" data-mp="3bepplv0arxr9">
        <div className="overflow-x-auto" data-id="element-247" data-mp="31r3x3j5l60vh">
          <table className="w-full min-w-[1100px] border-collapse text-left" data-id="element-248" data-mp="36o40fmb1wpiw">
            <thead className="bg-slate-50" data-id="element-249" data-mp="2soci59di4mz3">
              <tr className="border-b border-slate-200" data-id="element-250" data-mp="2spnjz3ndzknk">
                <th className="min-w-[260px] px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500" data-id="element-251" data-mp="2wp4r01igopuu">
                  Department
                </th>
                {months.map(month => <th key={month.key} className="min-w-[140px] px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500" data-id="element-252" data-mp="3pelhuuqfnyoy">
                    {month.label}
                  </th>)}
              </tr>
              <tr className="border-b border-slate-200 bg-slate-100/70" data-id="element-253" data-mp="78gtii9artvq">
                <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600" data-id="element-254" data-mp="bz701ytgm2s0">
                  Monthly total
                </th>
                {monthlyTotals.map((total, index) => <th key={months[index].key} className="px-4 py-2.5 text-right text-xs font-semibold tabular-nums text-slate-700" data-id="element-255" data-mp="1rrattpcmc4ae" data-mpi={index}>
                    {formatCurrency(total)}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-256" data-mp="1ds1hj0rj9ubp">
              {activeDepartments.map(department => <tr key={department.id} className="bg-white hover:bg-slate-50" data-id="element-257" data-mp="18t6wrta09voy">
                  <td className="px-4 py-3 text-sm font-medium text-slate-900" data-id="element-258" data-mp="1nq7h3bpvaode">
                    {department.name}
                  </td>
                  {months.map(month => <td key={month.key} className="p-0" data-id="element-259" data-mp="2z3te1j1yxu9h">
                      <BudgetCell departmentName={department.name} monthLabel={month.label} value={valueFor(department.id, month.key)} onCommit={amount => settingsActions.updateDepartmentBudget(department.id, month.key, amount)} data-id="element-260" data-mp="2z6fmrm0lifta" />
                    </td>)}
                </tr>)}
            </tbody>
          </table>
        </div>
        {activeDepartments.length === 0 && <div className="border-t border-slate-100 px-5 py-10 text-center text-sm text-slate-500" data-id="element-261" data-mp="1rk8t6061im3n">
            Add or reactivate a department to allocate its budget.
          </div>}
      </div>
    </section>;
}
function MonthNavigation({
  onPrevious,
  onCurrent,
  onNext
}: {
  onPrevious: () => void;
  onCurrent: () => void;
  onNext: () => void;
}) {
  return <div className="inline-flex items-center self-start rounded-lg border border-slate-200 bg-white p-1 sm:self-auto" aria-label="Budget month navigation" data-id="element-262" data-mp="2adctu5hodz7r">
      <button type="button" onClick={onPrevious} className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-100" aria-label="Show earlier months" data-id="element-263" data-mp="2fh5hi9v6wtfr">
        <ChevronLeftIcon className="h-4 w-4" data-id="element-264" data-mp="26itfzcsscb8j" />
      </button>
      <button type="button" onClick={onCurrent} className="px-2 text-xs font-medium text-[#14466B] hover:underline focus:outline-none focus:ring-2 focus:ring-blue-100" data-id="element-265" data-mp="335oeht8shjsj">
        Current month
      </button>
      <button type="button" onClick={onNext} className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-100" aria-label="Show later months" data-id="element-266" data-mp="2tf3lclbvx7h0">
        <ChevronRightIcon className="h-4 w-4" data-id="element-267" data-mp="3h5pr25yf2fnu" />
      </button>
    </div>;
}
function BudgetCell({
  departmentName,
  monthLabel,
  value,
  onCommit
}: {
  departmentName: string;
  monthLabel: string;
  value: number;
  onCommit: (amount: number) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value ? value.toString() : '');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!isEditing) setDraft(value ? value.toString() : '');
  }, [isEditing, value]);
  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);
  const commit = () => {
    const amount = Number(draft);
    if (draft.trim() === '' || Number.isNaN(amount) || amount < 0) {
      onCommit(0);
    } else if (amount !== value) {
      onCommit(amount);
    }
    setIsEditing(false);
  };
  if (isEditing) {
    return <input ref={inputRef} type="number" min="0" step="0.01" value={draft} onChange={event => setDraft(event.target.value)} onBlur={commit} onKeyDown={event => {
      if (event.key === 'Enter') event.currentTarget.blur();
      if (event.key === 'Escape') {
        setDraft(value ? value.toString() : '');
        setIsEditing(false);
      }
    }} className="min-h-[48px] w-full bg-blue-50 px-4 py-3 text-right text-sm tabular-nums text-slate-900 outline-none focus:ring-2 focus:ring-inset focus:ring-blue-400" aria-label={`Budget for ${departmentName} in ${monthLabel}`} data-id="element-268" data-mp="2gzdom0406yfx" />;
  }
  return <button type="button" onClick={() => setIsEditing(true)} className="flex min-h-[48px] w-full items-center justify-end px-4 py-3 text-right text-sm tabular-nums text-slate-600 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-400" aria-label={`Edit budget for ${departmentName} in ${monthLabel}`} data-id="element-269" data-mp="p1atq96om7dw">
      {value > 0 ? formatCurrency(value) : <span className="text-slate-300" data-id="element-270" data-mp="2dyvgowft4blq">—</span>}
    </button>;
}
function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0
  }).format(value);
}
function monthKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}